import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { createSelectors } from './create-selectors';

interface State {
  favoriteFoodItemIndex: Record<number, boolean>;
  hasHydrated: boolean;
}

interface Actions {
  setFoodItemFavorited: (foodItemId: number, isFavorited: boolean) => void;
  hydrateFoodItemFavorites: (foodItemIds: number[]) => void;
  isFoodItemFavorited: (foodItemId: number) => boolean;
}

export const INITIAL_STATE: State = {
  favoriteFoodItemIndex: {},
  hasHydrated: false,
};

export const useFoodItemFavoritesStore = create(
  persist(
    immer<State & Actions>((set, get) => ({
      ...INITIAL_STATE,
      setFoodItemFavorited: (foodItemId, isFavorited) => {
        set((state) => {
          state.favoriteFoodItemIndex[foodItemId] = isFavorited;
        });
      },
      // One-shot: later refetches must not clobber optimistic toggles before the read replica catches up.
      hydrateFoodItemFavorites: (foodItemIds) => {
        if (get().hasHydrated) return;
        const next: Record<number, boolean> = {};
        foodItemIds.forEach((id) => {
          next[id] = true;
        });
        set({ favoriteFoodItemIndex: next, hasHydrated: true });
      },
      isFoodItemFavorited: (foodItemId) => Boolean(get().favoriteFoodItemIndex[foodItemId]),
    })),
    {
      name: 'food-item-favorites-storage',
      getStorage: () => localStorage,
      // Don't persist hasHydrated — every session re-syncs from server before locking in.
      partialize: (state) => ({
        favoriteFoodItemIndex: state.favoriteFoodItemIndex,
      }),
    }
  )
);

export const useFoodItemFavoritesStoreSelectors = createSelectors(useFoodItemFavoritesStore);
